// Library Methods
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

// Components
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import SelectVideoButton from "./SelectVideoButton/SelectVideoButton";
import VideoSliderCard from "./VideoSliderCard/VideoSliderCard";

// Styles
import { getSurfaceTokens } from "src/styles/mui/muiStylesHelper";

// Contexts
import { useClinicContext } from "src/contexts/clinic/ClinicProvider";
import { useExamSliderContext } from "src/contexts/exams/ExamSliderContext";

// Constants
import { examCardEnums } from "src/constants/enums";

// Hooks
import { useClinicVideos } from "src/services/clinics/hooks/clinic-videos/useClinicVideos";

const VideoSliderList = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { onSurface } = getSurfaceTokens(theme);
  const { clinicInfo } = useClinicContext();
  const { selectedExamCards } = useExamSliderContext();

  const {
    data: videoData,
    isLoading: isLoadingVideos,
    isError: isErrorVideos,
  } = useClinicVideos(clinicInfo?.id);

  const hasNoVideos = useMemo(
    () => !isLoadingVideos && !isErrorVideos && !videoData?.videos?.length,
    [isLoadingVideos, isErrorVideos, videoData]
  );

  const hasVideos = useMemo(
    () =>
      !isLoadingVideos &&
      !isErrorVideos &&
      videoData &&
      videoData?.videos?.length > 0,
    [isLoadingVideos, isErrorVideos, videoData]
  );

  // Only selected videos get an actual player mounted
  const selectedVideoCards = useMemo(
    () =>
      selectedExamCards.filter(
        (card) => card.examFieldType === examCardEnums.VIDEO.algorithmPath
      ),
    [selectedExamCards]
  );

  return (
    <Grid item display="flex" flexDirection="column" gap="16px">
      {hasNoVideos && (
        <Grid item height="82px" alignContent="center">
          <Typography color={onSurface} fontSize="16px" textAlign="center">
            {t("No Videos Found.")}
          </Typography>
        </Grid>
      )}

      {isLoadingVideos && (
        <Grid item height="82px" alignContent="center">
          <Typography color={onSurface} fontSize="16px" textAlign="center">
            {t("Loading...")}
          </Typography>
        </Grid>
      )}

      {hasVideos && (
        <Grid
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "repeat(1, 1fr)",
              sm: "repeat(2, 1fr)",
              md: "repeat(3, 1fr)",
            },
            gap: "8px",
          }}
        >
          {videoData?.videos
            ?.sort((a, b) => a?.videoName?.localeCompare(b?.videoName))
            .map((item) => (
              <SelectVideoButton
                key={item.id}
                label={item.videoName}
                videoId={item.id}
              />
            ))}
        </Grid>
      )}

      <Grid item display="flex" flexDirection="column" gap="16px">
        {hasVideos && selectedVideoCards.length === 0 && (
          <Grid item height="82px" alignContent="center">
            <Typography color={onSurface} fontSize="16px" textAlign="center">
              {t("No Videos Selected.")}
            </Typography>
          </Grid>
        )}

        <Grid
          item
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "repeat(1, 1fr)",
              sm: "repeat(3, 1fr)",
            },
            gap: "8px",
          }}
        >
          {selectedVideoCards.map((card) => {
            const video = videoData?.videos.find(
              (item) => item.id === card.videoId
            );

            if (!video) return null;

            return (
              <VideoSliderCard
                key={`${card.videoId}-${card.groupId}`}
                label={video.videoName}
                url={video.videoUrl}
                groupId={card.groupId}
              />
            );
          })}
        </Grid>
      </Grid>
    </Grid>
  );
};

export default VideoSliderList;
